import type {ICreditSubscription, ICreditsBalance} from "../../models/interfaces/credits.interface.ts";

export const PLAN_NAMES: Record<ICreditSubscription["planId"], string> = {pro: "Pro", business: "Business", scale: "Scale"};
const utcFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});
// Period boundaries are UTC instants (C1); the label says so because renewals are checked against the job log.
// The no-break space keeps "UTC" on the same line as its time.
const formatUtc = (iso: string): string => `${utcFormatter.format(new Date(iso))}\u00a0UTC`;
export const formatCredits = (credits: number): string => credits.toLocaleString("en-US");
export const tabularNums = {fontVariantNumeric: "tabular-nums"} as const;

// DESIGN.md motion: 120ms press scale, 200ms color fades; reduced motion keeps only the fades.
const pressTransition = {
  transition: "transform 120ms cubic-bezier(0.2, 0, 0, 1), background-color 200ms linear, border-color 200ms linear",
  _mediaReduceMotion: {transition: "background-color 200ms linear, border-color 200ms linear"},
} as const;

// The app runs in Chakra dark mode; the white panel needs explicit light colors on every control.
export const solidButtonStyle = {
  ...pressTransition,
  size: "md",
  rounded: "lg",
  fontSize: "sm",
  fontWeight: "semibold",
  bg: "brandPurple.500",
  color: "white",
  _hover: {bg: "violetPurple.500"},
  _active: {bg: "brandPurple.600", transform: "scale(0.96)"},
} as const;

export const outlineButtonStyle = {
  ...pressTransition,
  size: "md",
  rounded: "lg",
  fontSize: "sm",
  fontWeight: "semibold",
  variant: "outline",
  bg: "white",
  borderColor: "n.300",
  color: "n.700",
  _hover: {bg: "n.200"},
  _active: {bg: "n.300", transform: "scale(0.96)"},
} as const;

// Sits on the n.200 summary bar, so its washes step one tone darker than the bar.
export const textButtonStyle = {
  ...pressTransition,
  variant: "ghost",
  h: 10,
  px: 3,
  rounded: "lg",
  fontSize: "sm",
  fontWeight: "semibold",
  color: "brandPurple.500",
  _hover: {bg: "n.300"},
  _active: {bg: "n.300", transform: "scale(0.96)"},
} as const;

export const statusBadgeStyle = {
  rounded: "full",
  px: 2,
  fontSize: "xs",
  fontWeight: "semibold",
  textTransform: "none",
} as const;

// Only a live subscription is a current plan: cancelled or expired rows and a legacy plan label without a
// subscription (pre-core purchases) never imply one.
export const currentSubscription = (balance: ICreditsBalance): ICreditSubscription | null =>
  balance.subscription?.live ? balance.subscription : null;

const planNameFor = (balance: ICreditsBalance, subscription: ICreditSubscription): string =>
  balance.planName ?? PLAN_NAMES[subscription.planId];

export const planFraction = (balance: ICreditsBalance): string =>
  `${formatCredits(balance.planRemainingCredits)}/${formatCredits(balance.planGrantedCredits)}`;

export const periodLabel = (balance: ICreditsBalance): string => `${planFraction(balance)} left this period`;

export const periodLeftPercent = (balance: ICreditsBalance): number =>
  balance.planGrantedCredits === 0
    ? 0
    : Math.min(100, Math.max(0, Math.round((100 * balance.planRemainingCredits) / balance.planGrantedCredits)));

type StatusTone = "active" | "warning" | "ending" | "unlimited";

export const badgeStyles: Record<StatusTone, {bg: string; color: string}> = {
  active: {bg: "success.100", color: "success.900"},
  warning: {bg: "sunsetAmber.100", color: "sunsetAmber.800"},
  ending: {bg: "n.300", color: "n.700"},
  unlimited: {bg: "brandPurple.100", color: "brandPurple.700"},
};

const statusFor = (subscription: ICreditSubscription): {label: string; tone: StatusTone} => {
  if (subscription.status === "past_due") return {label: "Past due", tone: "warning"};
  if (subscription.cancelAtPeriodEnd) return {label: "Cancels at period end", tone: "ending"};
  return {label: "Active", tone: "active"};
};

export const planStatusFor = (
  balance: ICreditsBalance,
  subscription: ICreditSubscription | null,
): {label: string; tone: StatusTone} | null => {
  if (balance.unlimited) return {label: "Unlimited", tone: "unlimited"};
  return subscription ? statusFor(subscription) : null;
};

export const planHeadingFor = (balance: ICreditsBalance, subscription: ICreditSubscription | null): string => {
  if (balance.unlimited) return balance.planName ?? "Enterprise";
  return subscription ? planNameFor(balance, subscription) : "No active subscription";
};

// A member holds no billing action at all: both views stay read-only and lead with MEMBER_NOTICE.
export const isReadOnly = (balance: ICreditsBalance): boolean => !Object.values(balance.allowedActions).some(Boolean);

export const dateLineFor = (subscription: ICreditSubscription): string => {
  if (subscription.cancelAtPeriodEnd) return `Ends ${formatUtc(subscription.periodEnd)}`;
  if (subscription.status === "past_due") {
    return subscription.nextActionAt
      ? `Renewal payment failed - next attempt ${formatUtc(subscription.nextActionAt)}`
      : "Renewal payment failed";
  }
  return `Renews ${formatUtc(subscription.periodEnd)}`;
};
